import { useEffect, useMemo, useState } from 'react';

import { useSetAtom } from 'jotai';
import { ActionMenu, FilterSearch, Flex, Group, SegmentedControl } from 'ui';

import { useCreateInitialSearchObject } from '../Search/useInitialQuerySearch';
import { useTableContext } from '../table.context';
import { TableSearchFields } from '../types/searchFieldsTypes';
import { TableHeaderProps, TableHeaderSegmentedProps } from '../types/types';
import { OptionalFields } from './components/OptionalFields';
import {
  TableComparisonInput,
  TableDateInput,
  TableMultiSelectInput,
  TableSearchInput,
  TableSearchNumberInput,
  TableSelectInput,
} from './components/TableHeaderInputs';
import { TableSearchQueryClient } from './components/TableSearchQueryClient';
import { TableExport } from './components/export/TableExport';
import { SearchableMultiSelectField, SearchableSelectField } from './components/searchables';

// Field components are used to render the search fields
const FieldComponents = {
  search: TableSearchInput,
  dateRange: TableDateInput,
  select: TableSelectInput,
  multi_select: TableMultiSelectInput,
  searchable_select: SearchableSelectField,
  searchable_multi: SearchableMultiSelectField,
  number: TableSearchNumberInput,
  comparison: TableComparisonInput,
};

// Depending on the type of search field (search, dateRange, filter),
// we render a different component
const SearchFields = ({
  searchFields,
  withinPortal = false,
  rootRef,
  showInParams = true,
}: {
  withinPortal?: boolean;
  searchFields: TableSearchFields<any>;
  rootRef?: React.RefObject<HTMLDivElement> | null;
  showInParams?: boolean;
}) => {
  const { resetTableSearches } = useTableContext();
  const { getInitialValue } = useCreateInitialSearchObject();

  const fields = useMemo(
    () =>
      searchFields.map(([type, props], idx) => {
        const Component = FieldComponents[type];

        // Since a filter menu can have multiple other kinds of fields,
        // this is recursively rendered
        if (type === 'filter') {
          return (
            <FilterSearch keepMounted onReset={resetTableSearches} key={`filter`}>
              {/*
          Note:  Always have filtered searchfield to  be defined within a portal, this allows it to be rendered over the filter
          however, we must always pass our rootRef to prevent closing when clicking within the portals.
          */}
              <FilterSearch.Item>
                {(rootRef) => (
                  <SearchFields searchFields={props} withinPortal={true} rootRef={rootRef} />
                )}
              </FilterSearch.Item>
            </FilterSearch>
          );
        }
        if (props.visible === false) return null;
        if (type === 'optional_fields') {
          return (
            <OptionalFields
              useCreateCustomFilterOptions={props.useCreateCustomFilterOptions}
              label={props.label}
              key={`optional-${idx}`}
            >
              {(searchFields) => (
                <SearchFields
                  key={`optional-search`}
                  searchFields={searchFields}
                  withinPortal={withinPortal}
                  rootRef={rootRef}
                  showInParams={false}
                />
              )}
            </OptionalFields>
          );
        }

        if ('fields' in props) {
          const initialQueryValue = getInitialValue(props.fields);

          return (
            <Component
              key={props.fields.join('')}
              {...props}
              fields={props.fields}
              comboboxProps={{ withinPortal: withinPortal }}
              rootRef={rootRef}
              initialValue={initialQueryValue ?? props.initialValue}
              showInParams={showInParams}
            />
          );
        }
        return null;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootRef, searchFields, showInParams, withinPortal]
  );

  return fields;
};

/**
 *
 * Provides a dynamic segmented control that allows
 * 1. Fully controlled, value and onChange are passed in via props
 * 2. Fully uncontrolled, neither value nor onChange are passed in via props
 * 3. Partially controlled, Initial value is passed in via props, neither value nor onChange are passed in via props
 * 4. Hooked in, Where value is not passed in but onChange is passed in via props allowing middleware to be added
 */
const LeftSideSegmentedControl = ({
  value,
  initialValue,
  onChange,
  ...segmentedControlProps
}: TableHeaderSegmentedProps) => {
  const { setHeaderSegmentedControlAtom, atomStore, resetTableSearches, resetPagination } =
    useTableContext();
  const [_value, _setValue] = useState<string | undefined>(initialValue);
  const setTableHeader = useSetAtom(setHeaderSegmentedControlAtom, { store: atomStore });

  const handleChange = (value: string) => {
    if (typeof value === 'string') {
      if (onChange) {
        // Manually defining what we could need rather than blindly passing all ctx as that could
        // Lead to misuses of the context
        onChange(value, { resetTableSearches, resetPagination });
      }
      _setValue(value);
    }
  };

  useEffect(() => {
    const finalValue = value ?? _value;
    if (finalValue) {
      setTableHeader(finalValue);
    }
  }, [_value, setTableHeader, value]);

  return (
    <SegmentedControl
      value={value ?? _value}
      onChange={handleChange}
      variant="subtle"
      size="sm"
      w={335}
      {...segmentedControlProps}
    />
  );
};

const TableHeaderLeftSide = ({
  title,
  leftSide,
  segmentedControlProps,
}: Pick<TableHeaderProps<any>, 'title' | 'leftSide' | 'segmentedControlProps'>) => {
  const hasLeftSide = Boolean(leftSide);

  if (hasLeftSide) return leftSide;
  if (segmentedControlProps) return <LeftSideSegmentedControl {...segmentedControlProps} />;
  return (
    <Flex fw={600} c="gray.8">
      {/* Returning null specifically to avoid booleans */}
      {title ? title : null}
    </Flex>
  );
};

const TableHeader = ({
  searchFields,
  title,
  exportable,
  header,
  leftSide,
  rightSide,
  segmentedControlProps,
  withSearchParams = false,
  Actions,
}: TableHeaderProps<any>) => {
  const hasCustomHeader = useMemo(() => Boolean(leftSide || rightSide), [leftSide, rightSide]);
  const shouldShowFields = useMemo(
    () => Boolean((searchFields && searchFields.length > 0) || exportable),
    [exportable, searchFields]
  );
  if (header) return <>{header}</>;
  if (!shouldShowFields && !title && !hasCustomHeader) return null;

  return (
    <Group
      justify="space-between"
      align="center"
      p="md"
      bg="white"
      style={(theme) => ({
        borderTopLeftRadius: theme.radius.md,
        borderTopRightRadius: theme.radius.md,
      })}
    >
      <TableHeaderLeftSide
        title={title}
        leftSide={leftSide}
        segmentedControlProps={segmentedControlProps}
      />

      {rightSide
        ? rightSide
        : shouldShowFields && (
            <Group align="normal" justify="center" wrap="nowrap">
              <ActionMenu Actions={Actions} />
              <SearchFields searchFields={searchFields ?? []} />
              <TableExport />
            </Group>
          )}
      {withSearchParams ? <TableSearchQueryClient /> : null}
    </Group>
  );
};

TableHeader.SearchInput = TableSearchInput;
TableHeader.SelectInput = TableSelectInput;
TableHeader.SearchFields = SearchFields;

export { SearchFields, TableHeader };
